import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUSES = ["NEW", "CONTACTED", "NEGOTIATING", "CONVERTED", "CLOSED"];
const STATUS_STYLES = {
  NEW: "bg-blue-100 text-blue-800",
  CONTACTED: "bg-amber-100 text-amber-800",
  NEGOTIATING: "bg-purple-100 text-purple-800",
  CONVERTED: "bg-green-100 text-green-800",
  CLOSED: "bg-slate-100 text-slate-600",
};

export default function AdminSalesRequests() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [leadType, setLeadType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (leadType) params.set("lead_type", leadType);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/admin/sales-requests?${params}`).then((r) => setRows(r.data.requests)).catch((e) => toast.error(formatApiError(e)));
  }, [q, status, leadType, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const changeStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/sales-requests/${id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace(/_/g, " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sales-requests-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Sales Requests</h1>
        <p className="mt-1 text-sm text-muted-foreground">Custom Enterprise Plan enquiries from the public pricing page.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="sales-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, company, email, WhatsApp" className="w-72 bg-white pl-8" />
        </div>
        <select data-testid="sales-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="sales-leadtype-filter" value={leadType} onChange={(e) => setLeadType(e.target.value)}
          className="rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm">
          <option value="">All lead types</option>
          <option value="CUSTOM_PLAN_SALES_REQUEST">Custom Plan</option>
        </select>
        <Input data-testid="sales-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 bg-white" />
        <span className="text-sm text-muted-foreground">to</span>
        <Input data-testid="sales-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 bg-white" />
      </div>

      {!rows ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white" data-testid="sales-requests-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">WhatsApp</th>
                <th className="px-4 py-3 font-medium">Lead Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Date Submitted</th>
                <th className="px-4 py-3 font-medium">Last Updated</th>
                <th className="px-4 py-3 font-medium text-right">Update Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground" data-testid="sales-requests-empty">No sales requests found.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`sales-request-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.full_name}</td>
                  <td className="px-4 py-3">{r.company_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.whatsapp_number}</td>
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">Custom Plan</span></td>
                  <td className="px-4 py-3">
                    <span data-testid={`sales-status-${r.id}`} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status] || ""}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                    {r.status_history?.length > 0 && (
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {r.status_history[r.status_history.length - 1].old_status.replace(/_/g, " ")} → {r.status_history[r.status_history.length - 1].new_status.replace(/_/g, " ")} by {r.status_history[r.status_history.length - 1].changed_by}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDateTime(r.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDateTime(r.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <select data-testid={`sales-status-select-${r.id}`} value={r.status}
                      onChange={(e) => changeStatus(r.id, e.target.value)}
                      className="rounded-md border border-input bg-white px-2 py-1 text-xs shadow-sm">
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
